import { Directive, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Máscara de separador de miles para inputs numéricos.
 *
 * Se usa sobre un <input type="text"> con [(ngModel)]: el MODELO sigue siendo un
 * number (o null si está vacío) —así el resto de la lógica y lo que se envía al
 * backend no cambian— y la directiva solo formatea lo que se ve (miles con coma,
 * decimal con punto, locale es-MX). El cursor se conserva al reformatear.
 *
 * [decimals] = decimales permitidos (0 = enteros, p. ej. litros).
 * [fixedDecimals] = true → al formatear (blur / valor programático) SIEMPRE se
 * muestran los [decimals] decimales, rellenando con ceros ("21.7" → "21.70");
 * mientras se teclea no se fuerza nada. Para montos que exigen decimales fijos
 * (p. ej. costo por litro).
 */
@Directive({
  selector: 'input[appThousands]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ThousandsSeparatorDirective),
      multi: true,
    },
  ],
})
export class ThousandsSeparatorDirective implements ControlValueAccessor {
  @Input() decimals = 0;
  @Input() fixedDecimals = false;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    const prev = input.value;
    const caret = input.selectionStart ?? prev.length;
    // Se cuentan los caracteres que SOBREVIVEN al formateo (dígitos + el primer
    // punto decimal), no solo dígitos: contando solo dígitos, al teclear el "."
    // el caret se restauraba ANTES del punto y los siguientes dígitos caían en
    // la parte entera (bug 2026-07-10 "Costo por litro").
    let keptBefore = this.countKept(prev.slice(0, caret));
    // "." tecleado sin parte entera: formatWhileTyping antepone un "0" sintético
    // ("." → "0."); se suma 1 para que el caret lo salte y quede tras el punto.
    if (this.decimals > 0 && prev.replace(/[^\d.]/g, '').startsWith('.') && keptBefore > 0) {
      keptBefore += 1;
    }

    const num = this.parse(prev);
    const formatted = this.formatWhileTyping(prev);
    input.value = formatted;

    const pos = this.caretForKept(formatted, keptBefore);
    input.setSelectionRange(pos, pos);

    this.onChange(num);
  }

  @HostListener('blur')
  onBlur(): void {
    const num = this.parse(this.el.nativeElement.value);
    this.el.nativeElement.value = num == null ? '' : this.formatNumber(num);
    this.onTouched();
  }

  writeValue(value: number | null): void {
    this.el.nativeElement.value =
      value == null || !Number.isFinite(value) ? '' : this.formatNumber(value);
  }
  registerOnChange(fn: (value: number | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.el.nativeElement.disabled = isDisabled; }

  // ---------- helpers ----------
  /** Caracteres del texto crudo que sobreviven al formateo: dígitos y el PRIMER
   *  punto decimal (los demás puntos se descartan, igual que en parse/format;
   *  con decimals=0 el punto tampoco sobrevive). */
  private countKept(s: string): number {
    let count = 0;
    let dotSeen = false;
    for (const ch of s) {
      if (/\d/.test(ch)) {
        count++;
      } else if (ch === '.' && !dotSeen && this.decimals > 0) {
        count++;
        dotSeen = true;
      }
    }
    return count;
  }

  /** Texto a número: quita separadores de miles y deja un solo punto decimal.
   *  En campos ENTEROS (decimals=0) el punto se ignora por completo — "14.000"
   *  tecleado a la europea es 14,000, no un decimal que colapsa a 14 (bug
   *  2026-07-13 "Costo total aparece mal": los litros quedaban en 14 y el
   *  total derivado salía microscópico). */
  private parse(raw: string): number | null {
    if (raw == null) { return null; }
    let s = raw.replace(/[^\d.]/g, '');
    if (this.decimals === 0) { s = s.replace(/\./g, ''); }
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    }
    if (s === '' || s === '.') { return null; }
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  /** Formatea mientras se escribe: miles en la parte entera, respeta el punto y los
   *  decimales que va tecleando (sin forzar decimales fijos). En campos enteros
   *  el punto se descarta como separador (espejo de parse). */
  private formatWhileTyping(raw: string): string {
    let s = raw.replace(/[^\d.]/g, '');
    if (this.decimals === 0) { s = s.replace(/\./g, ''); }
    const firstDot = s.indexOf('.');
    let intPart = firstDot === -1 ? s : s.slice(0, firstDot);
    let decPart = firstDot === -1 ? null : s.slice(firstDot + 1).replace(/\./g, '');
    if (this.decimals === 0) { decPart = null; }
    else if (decPart != null) { decPart = decPart.slice(0, this.decimals); }
    intPart = intPart.replace(/^0+(?=\d)/, '');
    const intFmt = intPart === '' ? '' : Number(intPart).toLocaleString('es-MX');
    if (firstDot === -1 || this.decimals === 0) { return intFmt; }
    return `${intFmt === '' ? '0' : intFmt}.${decPart}`;
  }

  /** Formato final (blur / writeValue): miles + hasta N decimales (exactamente
   *  N si fixedDecimals — los ceros de cierre se conservan: "21.70"). */
  private formatNumber(n: number): string {
    return n.toLocaleString('es-MX', {
      minimumFractionDigits: this.fixedDecimals ? this.decimals : 0,
      maximumFractionDigits: this.decimals,
    });
  }

  /** Posición del cursor tras `kept` caracteres significativos (dígitos y punto
   *  decimal) en el texto formateado. */
  private caretForKept(formatted: string, kept: number): number {
    if (kept <= 0) { return 0; }
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/[\d.]/.test(formatted[i])) { count++; }
      if (count === kept) { return i + 1; }
    }
    return formatted.length;
  }
}
